/**
 * Offline sign-in on a node that cannot reach the cloud (D20): the notice
 * that the link is down, and — once asked for — the break-glass form: an
 * account (the user's email, or `emergency` for the site's emergency account)
 * and a secret (the offline PIN or the emergency password).
 *
 * Presentational: whether to show it at all (the node's `GET /local/auth`),
 * and what a submit MEANS (`POST /local/login`), are the container's. The
 * typed values and whether the form is open are this component's own UI
 * state. Every attempt is audited on the node and reported to the cloud
 * once it is back — the notice says so, because an operator should know.
 */
import { type FormEvent, useState } from 'react';
import { Badge } from './ui/badge.js';
import { Button } from './ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card.js';
import { Field, FieldDescription, FieldError } from './ui/field.js';
import { Input } from './ui/input.js';
import { Label } from './ui/label.js';

export type OfflineSignInProps = {
  /** When the node last heard from the cloud (ISO), or null: never since it started. */
  readonly lastCloudContact: string | null;
  readonly pending: boolean;
  /** The node's refusal, as a sentence (wrong secret, locked, the link is back). */
  readonly error: string | null;
  /** Open the form at once (a story, or a page that has nothing else to offer). */
  readonly initiallyOpen?: boolean;
  readonly onSubmit: (credentials: { readonly account: string; readonly secret: string }) => void;
};

function lastContactText(lastCloudContact: string | null): string {
  if (lastCloudContact === null) return 'It has not reached the cloud since it started.';
  return `It last reached the cloud at ${new Date(lastCloudContact).toLocaleTimeString()}.`;
}

export function OfflineSignIn({ lastCloudContact, pending, error, initiallyOpen, onSubmit }: OfflineSignInProps) {
  const [open, setOpen] = useState(initiallyOpen === true);
  const [account, setAccount] = useState('');
  const [secret, setSecret] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit({ account: account.trim(), secret });
  };

  return (
    <Card className="w-full max-w-sm" data-testid="offline-sign-in">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Offline sign-in <Badge variant="offline">cloud unreachable</Badge>
        </CardTitle>
        <CardDescription data-testid="offline-notice">
          This node cannot reach the cloud. {lastContactText(lastCloudContact)} You can sign in with your offline PIN or the site’s emergency account.
          Every attempt is recorded and reported to your administrators.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {open ? (
          <form className="grid gap-4" onSubmit={submit} noValidate>
            <Field>
              <Label htmlFor="offline-account">Account</Label>
              <Input id="offline-account" type="text" autoComplete="username" value={account} onChange={(e) => setAccount(e.target.value)} disabled={pending} required />
              <FieldDescription>Your email, or “emergency” for the site’s emergency account.</FieldDescription>
            </Field>
            <Field>
              <Label htmlFor="offline-secret">Offline PIN or password</Label>
              <Input id="offline-secret" type="password" autoComplete="current-password" value={secret} onChange={(e) => setSecret(e.target.value)} disabled={pending} required />
            </Field>
            <FieldError>{error}</FieldError>
            <Button type="submit" disabled={pending || account.trim() === '' || secret === ''}>
              {pending ? 'Signing in…' : 'Sign in offline'}
            </Button>
          </form>
        ) : (
          <Button type="button" variant="outline" className="w-full" onClick={() => setOpen(true)}>
            Use offline sign-in
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
